import { useMutation } from '@tanstack/react-query';
import { isError } from 'lodash';

import { Tasks } from '@air/api';
import { matchesAirror, NOT_FOUND } from '@air/errors';
import { useToasts } from '@air/provider-toast';
import { isAirrorInstance } from '@air/utils-error';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useRefreshImportTasks } from '~/swr-hooks/importer/useRefreshImportTasks';

export const useResumeImportMutation = () => {
  const { showToast } = useToasts();
  const { currentWorkspace } = useCurrentWorkspace();
  const { refreshImports } = useRefreshImportTasks();

  const mutation = useMutation({
    mutationFn: async (importId: string) => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      const response = await Tasks.resumeTask({
        workspaceId,
        taskId: importId,
      });

      if (isAirrorInstance(response)) {
        throw response;
      }

      return importId;
    },
    onSuccess: () => {
      refreshImports();
    },
    onError: (error: unknown) => {
      if (matchesAirror(error, NOT_FOUND)) {
        showToast('Import not found.', { color: 'red' });
      } else {
        showToast(isError(error) ? error.message : 'There was an error resuming the import. Please try again later.', {
          color: 'red',
        });
      }
    },
  });

  return { mutation };
};
